/** ルートプラン画面で共有する整形・条件のヘルパー */

import { toast } from 'sonner'

import type {
  DesiredArrivalMode,
  PlanDayHours,
  RoutePlanItem,
  RoutePlanSummary,
  TravelMode,
} from '@/api/types/route-plan'
import { parseUtcDate } from '@/lib/date-utils'

export type PlanCondition = {
  title: string
  planDate: string
  departTime: string
  travelMode: TravelMode
  returnToOrigin: boolean
  defaultStayMinutes: number
  /** null なら時間で打ち切らない */
  maxDurationMinutes: number | null
  originLat: number | null
  originLon: number | null
  originName: string
  /** 出発地そのものに充てたプラン内の場所。作成画面では常に null */
  originPlaceId: number | null
  /** returnToOrigin=false のときだけ使う。未設定なら最後のスポットで終わる */
  destLat: number | null
  destLon: number | null
  destName: string
  /** 到着地そのものに充てたプラン内の場所 */
  destPlaceId: number | null
}

/** 秒 → 「9時間30分」。0 秒と null は表示しない */
export const formatDuration = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)
  if (hours === 0) return `${minutes}分`
  return minutes === 0 ? `${hours}時間` : `${hours}時間${minutes}分`
}

/** メートル → 「214km」。1km 未満は m のまま */
export const formatDistance = (meters: number | null): string | null => {
  if (meters === null || meters <= 0) return null
  return meters < 1000 ? `${Math.round(meters)}m` : `${Math.round(meters / 1000)}km`
}

/** 国内しか行かないので JST 固定。sv-SE ロケールは YYYY-MM-DD を返す */
export const toJstDateKey = (date: Date): string => date.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })

/** いつ行くか。plan_date が無ければ出発時刻の日付で代用する */
export const planDateKey = (plan: RoutePlanSummary): string | null => {
  if (plan.plan_date) return plan.plan_date
  return plan.depart_at ? toJstDateKey(parseUtcDate(plan.depart_at)) : null
}

/** 今日より前のプラン。日付が決まっていないものは含めない */
export const isPastPlan = (plan: RoutePlanSummary): boolean => {
  const key = planDateKey(plan)
  return key !== null && key < toJstDateKey(new Date())
}

/** 一覧の地図で経路を見分けるための色。プランは色を持たないので ID から決める */
const PLAN_PALETTE = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#0EA5E9', '#84CC16', '#F43F5E']

export const planColor = (planId: number): string => PLAN_PALETTE.at(planId % PLAN_PALETTE.length) ?? PLAN_PALETTE[0]

/** ローカル時刻の「日付 + 時刻」を UTC の ISO 文字列へ。バックエンドは aware datetime を期待する */
export const buildDepartAt = (planDate: string, departTime: string): string | null => {
  if (!planDate || !departTime) return null
  const parsed = new Date(`${planDate}T${departTime}:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

export const isConditionReady = (condition: PlanCondition): boolean =>
  condition.originLat !== null &&
  condition.originLon !== null &&
  buildDepartAt(condition.planDate, condition.departTime) !== null

/** 作成直後に詳細で日分けの画面を開かせる navigate state */
export const SPLIT_DAYS_STATE = { openSplitDays: true } as const

export const wantsSplitDays = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'openSplitDays' in state && state.openSplitDays === true

/** 1 回に送れるスポット数。バックエンドの PLAN_MAX_SPOTS と揃える */
export const PLAN_MAX_SPOTS = 200

/** 選択へまとめて足す。上限を超える分は捨てて知らせる */
export const addToSelection = (current: number[], adding: number[]): number[] => {
  const merged = [...new Set([...current, ...adding])]
  if (merged.length <= PLAN_MAX_SPOTS) return merged
  toast.warning(`一度に選べるのは ${PLAN_MAX_SPOTS} 件までです`)
  return merged.slice(0, PLAN_MAX_SPOTS)
}

const pad2 = (value: number): string => String(value).padStart(2, '0')

/** UTC の ISO 文字列 → ローカルの「HH:MM」。input[type=time] の値に使う */
export const toTimeInput = (iso: string | null): string => {
  if (!iso) return ''
  const parsed = parseUtcDate(iso)
  return `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`
}

/** UTC の ISO 文字列 → ローカルの「YYYY-MM-DD」 */
export const toDateInput = (iso: string | null): string => {
  if (!iso) return ''
  const parsed = parseUtcDate(iso)
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`
}

export const DESIRED_ARRIVAL_SUFFIX: Record<DesiredArrivalMode, string> = {
  at: 'ごろ',
  until: 'までに',
  after: '以降',
}

/** 到着希望を「12:00までに」の形にする。未設定なら null */
export const desiredArrivalLabel = (
  item: Pick<RoutePlanItem, 'desired_arrival_at' | 'desired_arrival_mode'>,
): string | null => {
  if (!item.desired_arrival_at || !item.desired_arrival_mode) return null
  return `${toTimeInput(item.desired_arrival_at)}${DESIRED_ARRIVAL_SUFFIX[item.desired_arrival_mode]}`
}

/** スポット 1 件を Google マップで開く URL。place_id があれば店舗ページが直接開く */
export const placeMapsUrl = (
  item: Pick<RoutePlanItem, 'name' | 'latitude' | 'longitude' | 'google_place_id'>,
): string => {
  const params = new URLSearchParams({ api: '1' })
  params.set(
    'query',
    item.latitude !== null && item.longitude !== null ? `${item.latitude},${item.longitude}` : item.name,
  )
  if (item.google_place_id) params.set('query_place_id', item.google_place_id)
  return `https://www.google.com/maps/search/?${params.toString()}`
}

/** どのラリーにも属さない場所。スタンプの概念がない */
export const hasRally = (item: Pick<RoutePlanItem, 'rally_items'>): boolean => item.rally_items.length > 0

/**
 * プラン当日の営業時間を「10:00〜20:00」の形にする。
 * 未取得なら null、その日が休みなら '定休日'。中休みのある店は「/」で並べる
 */
export const dayHoursLabel = (dayHours: PlanDayHours | null): string | null => {
  if (dayHours === null) return null
  if (dayHours.business_status === 'CLOSED_TEMPORARILY') return '臨時休業中'
  if (dayHours.business_status === 'CLOSED_PERMANENTLY') return '閉業'
  if (dayHours.closed) return '定休日'
  if (dayHours.open_24h) return '24時間営業'
  if (dayHours.periods.length === 0) return null
  return dayHours.periods
    .map((period) => `${period.open}〜${period.close_next_day ? '翌' : ''}${period.close}`)
    .join(' / ')
}

/** 紐づくラリーをすべて押し終えているか。ラリー外の場所は常に false */
export const isFullyStamped = (item: Pick<RoutePlanItem, 'rally_items'>): boolean =>
  hasRally(item) && item.rally_items.every((link) => link.stamped_at !== null)

/** 押せているラリー数 / 紐づくラリー数 */
export const stampedCount = (item: Pick<RoutePlanItem, 'rally_items'>): number =>
  item.rally_items.filter((link) => link.stamped_at !== null).length
